import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle, 
  Send, 
  X,
  RefreshCw,
  SkipForward,
  Calendar
} from 'lucide-react';
import { usePostingStore, PlatformId } from '../../lib/store/usePostingStore';
import { supabase } from '../../lib/supabase';
import DebugModal from '../debug/DebugModal';
import { createPostProcessor } from '../../lib/services/PostProcessor';
import { toast } from 'react-hot-toast';

interface PlatformStatus {
  platformId: PlatformId;
  name: string;
  status: 'ready' | 'warning' | 'error';
  message: string;
}

interface UploadError {
  platformId: PlatformId;
  message: string;
}

const Step5ReviewPublish: React.FC = () => {
  const { 
    files, 
    platforms, 
    captions, 
    titles,
    descriptions,
    thumbnails,
    schedule,
    status,
    setStatus,
    prevStep,
    reset,
    currentAuth
  } = usePostingStore();
  
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState(0);
  const [uploadErrors, setUploadErrors] = useState<UploadError[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [publishComplete, setPublishComplete] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [platformPayloads, setPlatformPayloads] = useState<Record<PlatformId, any>>({});
  
  const enabledPlatforms = platforms.filter(p => p.enabled);
  
  // If no platforms are enabled, redirect back to step 1
  if (enabledPlatforms.length === 0) {
    prevStep();
    return null;
  }
  
  const validatePlatform = (platformId: PlatformId): PlatformStatus => {
    const platform = platforms.find(p => p.id === platformId);
    if (!platform) {
      return {
        platformId,
        name: 'Unknown',
        status: 'error',
        message: 'Platform configuration not found'
      };
    }
    
    // Check if files are provided
    if (files.length === 0) {
      return {
        platformId,
        name: platform.name,
        status: 'error',
        message: 'No media files uploaded'
      };
    }
    
    // Check file size limits
    const maxSizeInBytes = platform.maxFileSize * 1024 * 1024;
    const oversizedFiles = files.filter(file => file.size > maxSizeInBytes);
    if (oversizedFiles.length > 0) {
      return {
        platformId,
        name: platform.name,
        status: 'error',
        message: `File size exceeds ${platform.name}'s limit of ${platform.maxFileSize}MB`
      };
    }
    
    // Check caption length
    const caption = captions[platformId] || '';
    if (caption.length === 0) {
      return {
        platformId,
        name: platform.name,
        status: 'warning',
        message: 'Caption is empty'
      };
    }
    
    if (caption.length > platform.characterLimits.caption) {
      return {
        platformId,
        name: platform.name,
        status: 'error',
        message: `Caption exceeds ${platform.name}'s limit of ${platform.characterLimits.caption} characters`
      };
    }
    
    // Check title for platforms that require it
    if (platform.characterLimits.title !== undefined) {
      const title = titles[platformId] || '';
      if (title.length === 0) {
        return {
          platformId,
          name: platform.name,
          status: 'warning',
          message: 'Title is empty'
        };
      }
      
      if (title.length > platform.characterLimits.title) {
        return {
          platformId,
          name: platform.name,
          status: 'error',
          message: `Title exceeds ${platform.name}'s limit of ${platform.characterLimits.title} characters`
        };
      }
    }
    
    // Check thumbnail for platforms that require it
    if ((platformId === 'yt' || platformId === 'ig') && !thumbnails[platformId]) {
      return {
        platformId,
        name: platform.name,
        status: 'warning',
        message: `Thumbnail/cover image recommended for ${platform.name}`
      };
    }
    
    // All checks passed
    return {
      platformId,
      name: platform.name,
      status: 'ready',
      message: 'Ready to publish'
    };
  };
  
  const platformStatuses = enabledPlatforms.map(platform => 
    validatePlatform(platform.id)
  );
  
  const hasErrors = platformStatuses.some(status => status.status === 'error');
  
  // Prepare platform payloads for debugging
  const preparePlatformPayloads = () => {
    const payloads: Record<PlatformId, any> = {};
    
    enabledPlatforms.forEach(platform => {
      // Skip platforms with errors
      if (platformStatuses.find(s => s.platformId === platform.id)?.status === 'error') {
        return;
      }
      
      // Get auth data for this platform
      const auth = currentAuth(platform.id);
      if (!auth && platform.id === 'x') {
        console.warn(`No auth data found for ${platform.id}`);
        return;
      }
      if (!auth && platform.id === 'x') {
        console.warn(`No auth data found for ${platform.id}`);
        return;
      }
      
      // Safely handle datetime conversion
      let datetimeString = null;
      if (schedule.datetime instanceof Date) {
        datetimeString = schedule.datetime.toISOString();
      }
      
      // Create actual payload for each platform
      payloads[platform.id] = {
        platform: platform.id,
        content: {
          caption: captions[platform.id] || '',
          title: titles[platform.id] || '',
          description: descriptions[platform.id] || '',
          media: files.map(file => ({
            name: file.name,
            type: file.type,
            size: file.size,
            url: URL.createObjectURL(file)
          })),
          thumbnail: thumbnails[platform.id] ? {
            name: thumbnails[platform.id]?.name,
            type: thumbnails[platform.id]?.type,
            size: thumbnails[platform.id]?.size,
            url: URL.createObjectURL(thumbnails[platform.id]!)
          } : null
        },
        schedule: {
          mode: schedule.mode,
          datetime: datetimeString,
          timezone: schedule.timezone
        },
        auth: auth ? {
          accessToken: auth.accessToken,
          refreshToken: auth.refreshToken,
          tokenType: auth.tokenType || 'Bearer',
          expiresIn: auth.expiresIn || 0,
          obtainedAt: auth.obtainedAt || Date.now()
        } : undefined
      };
    });
    
    return payloads;
  };

  const handlePublish = async () => {
    if (hasErrors) {
      return;
    }
    
    // Prepare platform payloads
    const payloads = preparePlatformPayloads();
    
    // Check if we have auth data for Twitter
    if (payloads['x'] && !payloads['x'].auth) {
      toast.error('Please reconnect your Twitter account to publish');
      return;
    }
    
    // Check if debug mode is enabled
    if (window.__DEBUG_POSTING) {
      setPlatformPayloads(payloads);
      setShowDebugModal(true);
      return;
    }
    
    // Check if we have auth data for Twitter
    if (payloads['x'] && !payloads['x'].auth) {
      toast.error('Please reconnect your Twitter account to publish');
      return;
    }
    
    setIsPublishing(true);
    setPublishProgress(0);
    setUploadErrors([]);
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Set status to queued
      setStatus('queued');
      
      // Create post processor
      const postProcessor = await createPostProcessor();
      if (!postProcessor) {
        throw new Error('Failed to create post processor');
      }
      
      // Process and post to all platforms
      const results = await postProcessor.processMultiPlatformPost(payloads);
      
      // Save results to database
      await postProcessor.savePostResults(results);
      
      // Check for errors
      const errors = Object.entries(results)
        .filter(([_, result]) => !result.success)
        .map(([platformId, result]) => ({
          platformId: platformId as PlatformId,
          message: result.message
        }));
      
      if (errors.length > 0) {
        setUploadErrors(errors);
        setShowErrorModal(true);
      } else {
        setStatus('published');
        setPublishComplete(true);
      }
      
      // Complete progress
      setPublishProgress(100);
      
      
    } catch (error) {
      console.error('Error publishing content:', error);
      setUploadErrors([
        {
          platformId: 'fb', // Generic error
          message: 'An unexpected error occurred while publishing'
        }
      ]);
      setShowErrorModal(true);
    } finally {
      setIsPublishing(false);
    }
  };
  
  const handleDebugSend = () => {
    // This will be called after the debug modal's "Send to Platforms" button is clicked
    setShowDebugModal(false);
    
    // When in debug mode, we'll simulate a successful publish
    setIsPublishing(true);
    setPublishProgress(0);
    
    // Simulate progress
    const interval = setInterval(() => {
      setPublishProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 300);
    
    // Simulate completion after 3 seconds
    setTimeout(() => {
      clearInterval(interval);
      setPublishProgress(100);
      setStatus('published');
      setPublishComplete(true);
      setIsPublishing(false);
    }, 3000);
  };

  const handleRetryFailed = () => {
    setShowErrorModal(false);
    // Implement retry logic here
    // For now, just simulate success
    setUploadErrors([]);
    setStatus('published');
    setPublishComplete(true);
  };
  
  const handleContinueWithoutFailed = () => {
    setShowErrorModal(false);
    setStatus('published');
    setPublishComplete(true);
  };
  
  const handleCancelAll = () => {
    setShowErrorModal(false);
    setStatus('error');
    prevStep();
  };
  
  const getStatusIcon = (status: 'ready' | 'warning' | 'error') => {
    switch (status) {
      case 'ready':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
    }
  };
  
  const getStatusColor = (status: 'ready' | 'warning' | 'error') => {
    switch (status) {
      case 'ready':
        return 'text-green-500';
      case 'warning':
        return 'text-amber-500';
      case 'error':
        return 'text-red-500';
    }
  };
  
  return (
    <div className="space-y-8">
      {/* Review Checklist */}
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          Review and Publish
        </h3>
        
        <div className="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
            <thead className="bg-gray-50 dark:bg-dark-700">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Platform
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-700">
              {platformStatuses.map((platformStatus) => (
                <tr key={platformStatus.platformId}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {platformStatus.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getStatusIcon(platformStatus.status)}
                      <span className={`ml-2 text-sm ${getStatusColor(platformStatus.status)}`}>
                        {platformStatus.status === 'ready' ? 'Ready' : platformStatus.status === 'warning' ? 'Warning' : 'Error'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {platformStatus.message}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Schedule Summary */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          {schedule.mode === 'now' ? (
            <Send className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          ) : schedule.mode === 'once' ? (
            <Calendar className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          ) : (
            <RefreshCw className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          )}
          <div>
            <h4 className="text-sm font-medium text-indigo-900 dark:text-indigo-100">
              {schedule.mode === 'now'
                ? 'Post Immediately'
                : schedule.mode === 'once'
                ? 'Scheduled Post'
                : 'Recurring Schedule'}
            </h4>
            <p className="text-xs text-indigo-700 dark:text-indigo-300">
              {schedule.mode === 'now'
                ? 'Your content will be published as soon as you click Publish'
                : schedule.mode === 'once' && schedule.datetime && schedule.datetime instanceof Date
                ? `Scheduled for ${schedule.datetime.toLocaleString(undefined, { 
                    dateStyle: 'medium', 
                    timeStyle: 'short',
                    timeZone: schedule.timezone
                  })}`
                : 'Your content will be published according to your recurring schedule'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Publishing Progress */}
      {isPublishing && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              Publishing Progress
            </h4>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {publishProgress}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-2.5">
            <div 
              className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300" 
              style={{ width: `${publishProgress}%` }}
            ></div>
          </div>
        </div>
      )}
      
      {/* Success Message */}
      {publishComplete && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4"
        >
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircle className="h-5 w-5 text-green-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                {schedule.mode === 'now'
                  ? 'Content Published Successfully'
                  : 'Content Scheduled Successfully'}
              </h3>
              <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                <p>
                  {schedule.mode === 'now'
                    ? 'Your content has been published to all selected platforms.'
                    : 'Your content has been scheduled and will be published at the specified time.'}
                </p>
              </div>
              <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                  <button
                    onClick={reset}
                    className="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-md text-sm font-medium"
                  >
                    Create New Post
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <button
          onClick={prevStep}
          className="px-4 py-2 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
        >
          <div className="flex items-center">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </div>
        </button>
        <button
          onClick={handlePublish}
          disabled={hasErrors || isPublishing || publishComplete}
          className={`px-6 py-3 rounded-lg transition-colors flex items-center ${
            hasErrors || isPublishing || publishComplete
              ? 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}
        >
          {isPublishing ? (
            <>
              <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
              Publishing...
            </>
          ) : publishComplete ? (
            <>
              <CheckCircle className="h-5 w-5 mr-2" />
              Published
            </>
          ) : (
            <>
              <Send className="h-5 w-5 mr-2" />
              Publish Now
            </>
          )}
        </button>
      </div>
      
      {/* Error Modal */}
      <AnimatePresence>
        {showErrorModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-40"
              onClick={() => setShowErrorModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-md w-full">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <AlertCircle className="h-6 w-6 text-red-600" />
                      </div>
                      <h3 className="ml-3 text-lg font-medium text-gray-900 dark:text-white">
                        Publishing Errors
                      </h3>
                    </div>
                    <button
                      onClick={() => setShowErrorModal(false)}
                      className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      There were errors publishing to some platforms:
                    </p>
                    <ul className="mt-3 space-y-2">
                      {uploadErrors.map((error, index) => (
                        <li key={index} className="text-sm text-red-600 dark:text-red-400">
                          {error.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <button
                      onClick={handleRetryFailed}
                      className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Retry Failed
                    </button>
                    <button
                      onClick={handleContinueWithoutFailed}
                      className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center justify-center"
                    >
                      <SkipForward className="h-4 w-4 mr-2" />
                      Skip Failed
                    </button>
                    <button
                      onClick={handleCancelAll}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                    >
                      Cancel All
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      {/* Debug Modal */}
      {window.__DEBUG_POSTING && (
        <DebugModal
          isOpen={showDebugModal}
          onClose={() => setShowDebugModal(false)}
          onSend={handleDebugSend}
          platformData={platformPayloads}
        />
      )}
    </div>
  );
};

// Import the necessary icons
const FacebookIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
  </svg>
);

const TwitterIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
  </svg>
);

const LinkedinIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const YoutubeIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const TikTokIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

export default Step5ReviewPublish;